import React, { useContext, useMemo, useState } from "react";

import SalesTable from "../AppPAges/InventoryManagement/SalesInvoice/SalesTable";
import useApi from "../../../utils/api-manager/Helper/useApi";
import useFormattingUtils from "../../../context/useFormattingUtils";
import AuthContext from "../../../utils/secure-route/AuthContext";
import { ToastContainer, toast } from "react-toastify";
import POSAppThermalInvoicePreview from "./POSAppThermalInvoicePreview";
import "./pos.styles.css";

function Transaction() {
  const { GetPaginatedData } = useApi();
  const { formatDate, formatCurrency } = useFormattingUtils();
  const { user } = useContext(AuthContext);

  const [salesInvoiceData, setSalesInvoiceData] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showThermalPreview, setShowThermalPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [tableRefreshKey, setTableRefreshKey] = useState(0);
  const [exportConfig, setExportConfig] = useState({
    datePreset: "this_month",
    fromDate: "",
    toDate: "",
    receiptColumns: ["invoice_no", "date", "pay_method", "total_qty", "total_amount"],
    itemColumns: ["item_name", "item_qty", "item_rate", "item_total"],
  });

  const rowsPerPage = 15;

  const safeNumber = (value) => {
    const number = parseFloat(value);
    return Number.isNaN(number) ? 0 : number;
  };

  const safeCurrency = (value) => {
    const amount = safeNumber(value);
    return formatCurrency ? formatCurrency(amount) : amount.toFixed(2);
  };

  const formatDateInput = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  };

  const parseDateValue = (value) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const startOfDay = (date) => {
    const next = new Date(date);
    next.setHours(0, 0, 0, 0);
    return next;
  };

  const endOfDay = (date) => {
    const next = new Date(date);
    next.setHours(23, 59, 59, 999);
    return next;
  };

  const addMonths = (date, amount) => {
    const next = new Date(date);
    next.setMonth(next.getMonth() + amount);
    return next;
  };

  const getDateRangeForPreset = (preset) => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();

    if (preset === "all") return { from: null, to: null, label: "All dates" };
    if (preset === "last_month") {
      return { from: startOfDay(new Date(year, month - 1, 1)), to: endOfDay(new Date(year, month, 0)), label: "Last month" };
    }
    if (preset === "this_quarter") {
      const quarterStartMonth = Math.floor(month / 3) * 3;
      return { from: startOfDay(new Date(year, quarterStartMonth, 1)), to: endOfDay(new Date(year, quarterStartMonth + 3, 0)), label: "This quarter" };
    }
    if (preset === "last_6_months") {
      return { from: startOfDay(addMonths(today, -6)), to: endOfDay(today), label: "Last 6 months" };
    }
    if (preset === "financial_year") {
      const fiscalStartYear = month >= 3 ? year : year - 1;
      return { from: startOfDay(new Date(fiscalStartYear, 3, 1)), to: endOfDay(new Date(fiscalStartYear + 1, 2, 31)), label: "This financial year" };
    }
    if (preset === "custom") {
      const fromDate = parseDateValue(exportConfig.fromDate);
      const toDate = parseDateValue(exportConfig.toDate);
      return { from: fromDate ? startOfDay(fromDate) : null, to: toDate ? endOfDay(toDate) : null, label: "Custom range" };
    }

    return { from: startOfDay(new Date(year, month, 1)), to: endOfDay(new Date(year, month + 1, 0)), label: "This month" };
  };

  const getLineItems = (invoice) => {
    const candidates = [
      invoice?.items,
      invoice?.products,
      invoice?.invoice_products,
      invoice?.pos_invoice_products,
      invoice?.sales_invoice_products,
      invoice?.product_details,
      invoice?.details,
    ];
    const found = candidates.find((candidate) => Array.isArray(candidate) && candidate.length > 0);
    return Array.isArray(found) ? found : [];
  };

  const getItemName = (item) =>
    item?.pos_item_title ||
    item?.product_title ||
    item?.title ||
    item?.name ||
    item?.product_name ||
    item?.product?.title ||
    item?.product?.name ||
    "Item";

  const getItemQty = (item) => safeNumber(item?.qty || item?.quantity || item?.sold_qty_stock || item?.total_qty);
  const getItemRate = (item) => safeNumber(item?.amount_per_item || item?.rate || item?.price || item?.selling_price || item?.amount);
  const getItemTotal = (item) => {
    const explicitTotal = safeNumber(item?.total_amount || item?.total || item?.after_discount || item?.line_total);
    return explicitTotal || getItemQty(item) * getItemRate(item);
  };

  const getInvoiceDate = (invoice) => parseDateValue(invoice?.raw_date || invoice?.date);

  const isInvoiceInRange = (invoice, range) => {
    const invoiceDate = getInvoiceDate(invoice);
    if (!invoiceDate) return !range.from && !range.to;
    if (range.from && invoiceDate < range.from) return false;
    if (range.to && invoiceDate > range.to) return false;
    return true;
  };

  const storeInfo = {
    store_name: user?.store_name || user?.store?.name || "Store Name",
    store_address: user?.store_address || user?.store?.address || "Store Address",
    store_phone: user?.store_phone || user?.phone || "Phone Number",
  };

  const handleRefresh = () => {
    setTableRefreshKey((prev) => prev + 1);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setTableRefreshKey((prev) => prev + 1);
  };

  const fetchPOSInvoiceData = async ({
    page = 1,
    searchTerm = "",
    sort = "",
    order = "",
  } = {}) => {
    const trimmedSearchTerm = String(searchTerm || searchQuery || "").trim();

    try {
      setLoading(true);

      const response = await GetPaginatedData("getPosInvoice", {
        page,
        search: trimmedSearchTerm || undefined,
        sort,
        order,
        queryParams: {
          page_size: rowsPerPage,
          ...(sort && { sort }),
        },
      });

      const results = Array.isArray(response?.data) ? response.data : [];
      const totalPages = Math.ceil((response?.totalCount || 0) / rowsPerPage) || 1;

      const formattedInvoiceData = results.map((invoice) => ({
        ...invoice,
        id: invoice.id,
        invoice_no: invoice.invoice_no,
        raw_date: invoice.date,
        date: invoice.date ? formatDate(invoice.date) : "N/A",
        qty: invoice.total_qty || invoice.qty || 0,
        total_qty: invoice.total_qty || invoice.qty || 0,
        total_amount: invoice.total_amount || invoice.payable_amount || 0,
        payable_amount: invoice.payable_amount || invoice.total_amount || 0,
        pay_method: invoice.pay_method || invoice.payment_method || "N/A",
      }));

      setSalesInvoiceData(formattedInvoiceData);

      return {
        data: formattedInvoiceData,
        totalPages,
      };
    } catch (error) {
      console.error("Error fetching POS app invoice data:", error);
      toast.error("Failed to load POS app transaction history.");
      setSalesInvoiceData([]);

      return {
        data: [],
        totalPages: 1,
      };
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoiceForPreview = async (invoiceId) => {
    let invoiceData = null;

    const directResponse = await GetPaginatedData("getPosInvoice", {
      page: undefined,
      queryParams: { id: invoiceId },
    });

    if (Array.isArray(directResponse?.data) && directResponse.data.length > 0) {
      invoiceData = directResponse.data.find((invoice) => invoice.id === invoiceId);
    }

    if (!invoiceData && directResponse?.data && typeof directResponse.data === "object") {
      invoiceData = directResponse.data.id === invoiceId ? directResponse.data : null;
    }

    if (!invoiceData) {
      const MAX_PAGES = 10;

      for (let page = 1; page <= MAX_PAGES; page += 1) {
        const chunk = await GetPaginatedData("getPosInvoice", {
          page,
          queryParams: {
            page_size: rowsPerPage,
          },
        });

        if (Array.isArray(chunk?.data)) {
          invoiceData = chunk.data.find((invoice) => invoice.id === invoiceId);
          if (invoiceData) break;
        }

        if (!chunk?.nextPage) break;
      }
    }

    return invoiceData;
  };

  const receiptExportColumns = [
    { key: "invoice_no", label: "Receipt No", getValue: (invoice) => invoice.invoice_no || "" },
    { key: "date", label: "Date", getValue: (invoice) => invoice.raw_date || invoice.date || "" },
    { key: "pay_method", label: "Payment Method", getValue: (invoice) => invoice.pay_method || invoice.payment_method || "N/A" },
    { key: "total_qty", label: "Total Qty", getValue: (invoice) => safeNumber(invoice.total_qty || invoice.qty) },
    { key: "total_amount", label: "Money Collected", getValue: (invoice) => safeNumber(invoice.total_amount || invoice.payable_amount).toFixed(2) },
  ];

  const itemExportColumns = [
    { key: "item_name", label: "Item Name", getValue: (_invoice, item) => (item ? getItemName(item) : "") },
    { key: "item_qty", label: "Item Qty", getValue: (_invoice, item) => (item ? getItemQty(item) : "") },
    { key: "item_rate", label: "Item Rate", getValue: (_invoice, item) => (item ? getItemRate(item).toFixed(2) : "") },
    { key: "item_total", label: "Item Total", getValue: (_invoice, item) => (item ? getItemTotal(item).toFixed(2) : "") },
  ];

  const datePresetOptions = [
    { value: "this_month", label: "This month" },
    { value: "last_month", label: "Last month" },
    { value: "this_quarter", label: "This quarter" },
    { value: "last_6_months", label: "Last 6 months" },
    { value: "financial_year", label: "This financial year" },
    { value: "all", label: "All dates" },
    { value: "custom", label: "Custom range" },
  ];

  const toggleExportColumn = (group, key) => {
    const groupKey = group === "item" ? "itemColumns" : "receiptColumns";
    setExportConfig((prev) => {
      const current = prev[groupKey] || [];
      return {
        ...prev,
        [groupKey]: current.includes(key)
          ? current.filter((columnKey) => columnKey !== key)
          : [...current, key],
      };
    });
  };

  const updateDatePreset = (datePreset) => {
    const range = getDateRangeForPreset(datePreset);
    setExportConfig((prev) => ({
      ...prev,
      datePreset,
      fromDate: datePreset === "custom" ? prev.fromDate : formatDateInput(range.from),
      toDate: datePreset === "custom" ? prev.toDate : formatDateInput(range.to),
    }));
  };

  const csvEscape = (value) => {
    const text = value === null || value === undefined ? "" : String(value);
    return `"${text.replace(/"/g, '""')}"`;
  };

  const downloadCsvFile = (filename, rows) => {
    const csvContent = rows.map((row) => row.map(csvEscape).join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const fetchAllInvoicesForExport = async () => {
    const pageSize = 100;
    const trimmedSearchTerm = String(searchQuery || "").trim();
    const firstResponse = await GetPaginatedData("getPosInvoice", {
      page: 1,
      search: trimmedSearchTerm || undefined,
      queryParams: { page_size: pageSize },
    });
    const firstData = Array.isArray(firstResponse?.data) ? firstResponse.data : [];
    const totalPages = Math.ceil((firstResponse?.totalCount || firstData.length || 0) / pageSize) || 1;
    const rows = [...firstData];

    for (let page = 2; page <= totalPages; page += 1) {
      const response = await GetPaginatedData("getPosInvoice", {
        page,
        search: trimmedSearchTerm || undefined,
        queryParams: { page_size: pageSize },
      });
      if (Array.isArray(response?.data)) rows.push(...response.data);
    }

    return rows.map((invoice) => ({
      ...invoice,
      id: invoice.id,
      invoice_no: invoice.invoice_no,
      raw_date: invoice.date,
      date: invoice.date ? formatDate(invoice.date) : "N/A",
      qty: invoice.total_qty || invoice.qty || 0,
      total_qty: invoice.total_qty || invoice.qty || 0,
      total_amount: invoice.total_amount || invoice.payable_amount || 0,
      payable_amount: invoice.payable_amount || invoice.total_amount || 0,
      pay_method: invoice.pay_method || invoice.payment_method || "N/A",
    }));
  };

  const handleGenerateCsv = async () => {
    const selectedReceiptColumns = receiptExportColumns.filter((column) => exportConfig.receiptColumns.includes(column.key));
    const selectedItemColumns = itemExportColumns.filter((column) => exportConfig.itemColumns.includes(column.key));

    if (selectedReceiptColumns.length === 0 && selectedItemColumns.length === 0) {
      toast.error("Please select at least one column for export.");
      return;
    }

    const range = getDateRangeForPreset(exportConfig.datePreset);
    if (exportConfig.datePreset === "custom" && (!range.from || !range.to)) {
      toast.error("Please select both start and end dates.");
      return;
    }
    if (range.from && range.to && range.from > range.to) {
      toast.error("Start date cannot be after end date.");
      return;
    }

    try {
      setExportLoading(true);
      const invoices = await fetchAllInvoicesForExport();
      const filteredInvoices = invoices.filter((invoice) => isInvoiceInRange(invoice, range));

      if (filteredInvoices.length === 0) {
        toast.info("No POS receipts found for the selected export range.");
        return;
      }

      const header = [
        ...selectedReceiptColumns.map((column) => column.label),
        ...selectedItemColumns.map((column) => column.label),
      ];
      const includeItems = selectedItemColumns.length > 0;
      const bodyRows = filteredInvoices.flatMap((invoice) => {
        const items = getLineItems(invoice);
        const rowsToBuild = includeItems && items.length > 0 ? items : [null];
        return rowsToBuild.map((item) => [
          ...selectedReceiptColumns.map((column) => column.getValue(invoice, item)),
          ...selectedItemColumns.map((column) => column.getValue(invoice, item)),
        ]);
      });

      const rangeSlug = range.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      downloadCsvFile(`pos-app-receipts-${rangeSlug || "export"}.csv`, [header, ...bodyRows]);
      toast.success(`Exported ${bodyRows.length} CSV row${bodyRows.length === 1 ? "" : "s"}.`);
      setShowExportModal(false);
    } catch (error) {
      console.error("Error exporting POS app receipts:", error);
      toast.error("Failed to generate CSV export.");
    } finally {
      setExportLoading(false);
    }
  };

  const handlePrint = async (invoiceId) => {
    try {
      setPreviewLoading(true);

      const invoiceData = await fetchInvoiceForPreview(invoiceId);

      if (invoiceData) {
        setSelectedInvoice(invoiceData);
        setShowThermalPreview(true);
      } else {
        toast.error("Invoice details not found.");
        console.warn(`Invoice ID ${invoiceId} not found`);
      }
    } catch (error) {
      console.error("Error fetching invoice preview:", error);
      toast.error("Failed to open thermal receipt preview.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const closeThermalPreview = () => {
    setShowThermalPreview(false);
    setSelectedInvoice(null);
  };

  const dashboardStats = useMemo(() => {
    const receiptCount = salesInvoiceData.length;

    const totalCollected = salesInvoiceData.reduce(
      (sum, invoice) => sum + safeNumber(invoice.total_amount || invoice.payable_amount),
      0
    );

    const totalQty = salesInvoiceData.reduce(
      (sum, invoice) => sum + safeNumber(invoice.qty || invoice.total_qty),
      0
    );

    const averageReceipt = receiptCount > 0 ? totalCollected / receiptCount : 0;

    return {
      receiptCount,
      totalCollected,
      totalQty,
      averageReceipt,
    };
  }, [salesInvoiceData]);

  const columns = [
    {
      field: "invoice_no",
      header: "Receipt No",
      body: (rowData) => (
        <div className="posapp-table-invoice-cell">
          <div className="posapp-table-invoice-no">{rowData.invoice_no || "-"}</div>
          <div className="posapp-table-invoice-sub">POS app receipt</div>
        </div>
      ),
    },
    {
      field: "total_qty",
      header: "Qty",
      body: (rowData) => (
        <span className="posapp-table-qty-pill">
          {rowData.total_qty || rowData.qty || 0}
        </span>
      ),
    },
    {
      field: "total_amount",
      header: "Money Collected",
      body: (rowData) => (
        <span className="posapp-table-money-pill">
          {safeCurrency(rowData.total_amount || rowData.payable_amount)}
        </span>
      ),
    },
    {
      field: "pay_method",
      header: "Payment",
      body: (rowData) => (
        <span className="posapp-table-payment-pill">
          {rowData.pay_method || "N/A"}
        </span>
      ),
    },
    {
      field: "date",
      header: "Date",
      body: (rowData) => (
        <div className="posapp-table-date">
          {rowData.raw_date ? formatDate(rowData.raw_date) : rowData.date || "N/A"}
        </div>
      ),
    },
  ];

  const renderStatCard = ({ title, value, subtitle, icon, tone = "default" }) => (
    <div className={`posapp-stat-card posapp-stat-${tone}`}>
      <div className="posapp-stat-label">
        <span className="posapp-stat-icon">{icon}</span>
        <span>{title}</span>
      </div>
      <div className="posapp-stat-value">{value}</div>
      <div className="posapp-stat-subtitle">{subtitle}</div>
    </div>
  );

  const exportRangePreview = getDateRangeForPreset(exportConfig.datePreset);

  return (
    <div className="posapp-page">
      <ToastContainer />

      <style>
        {`
          .posapp-page {
            min-height: 100%;
            background:
              radial-gradient(circle at top left, rgba(14, 165, 233, 0.13), transparent 34%),
              radial-gradient(circle at top right, rgba(16, 185, 129, 0.12), transparent 30%),
              linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
            padding: 10px 12px 24px;
          }

          .posapp-hero {
            background:
              linear-gradient(135deg, #0f172a 0%, #1e293b 58%, #0369a1 100%);
            border-radius: 28px;
            padding: 26px;
            color: #ffffff;
            margin-bottom: 18px;
            box-shadow: 0 24px 60px rgba(15, 23, 42, 0.23);
            position: relative;
            overflow: hidden;
          }

          .posapp-hero::after {
            content: "";
            position: absolute;
            width: 280px;
            height: 280px;
            right: -105px;
            top: -125px;
            border-radius: 50%;
            background: rgba(56, 189, 248, 0.16);
          }

          .posapp-hero-content {
            position: relative;
            z-index: 1;
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 22px;
            flex-wrap: wrap;
          }

          .posapp-badge {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            background: rgba(255, 255, 255, 0.13);
            border: 1px solid rgba(255, 255, 255, 0.24);
            color: #bae6fd;
            padding: 8px 13px;
            border-radius: 999px;
            font-size: 12px;
            font-weight: 950;
            margin-bottom: 13px;
          }

          .posapp-title {
            margin: 0;
            font-size: 32px;
            font-weight: 950;
            letter-spacing: -0.04em;
            line-height: 1.08;
          }

          .posapp-description {
            margin: 9px 0 0;
            max-width: 690px;
            color: rgba(255, 255, 255, 0.82);
            font-size: 14px;
            line-height: 1.6;
          }

          .posapp-actions {
            display: flex;
            gap: 10px;
            flex-wrap: wrap;
            justify-content: flex-end;
          }

          .posapp-light-btn,
          .posapp-secondary-btn,
          .posapp-primary-btn,
          .posapp-export-btn {
            border: none;
            border-radius: 999px;
            padding: 10px 16px;
            font-size: 13px;
            font-weight: 950;
            cursor: pointer;
            transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
            white-space: nowrap;
          }

          .posapp-light-btn:hover,
          .posapp-secondary-btn:hover,
          .posapp-primary-btn:hover,
          .posapp-export-btn:hover {
            transform: translateY(-1px);
          }

          .posapp-light-btn {
            background: #ffffff;
            color: #0f172a;
            box-shadow: 0 10px 24px rgba(15, 23, 42, 0.16);
          }

          .posapp-secondary-btn {
            background: rgba(255, 255, 255, 0.12);
            color: #ffffff;
            border: 1px solid rgba(255, 255, 255, 0.26);
          }

          .posapp-primary-btn {
            background: #0284c7;
            color: #ffffff;
          }

          .posapp-export-btn {
            background: #0f172a;
            color: #ffffff;
            box-shadow: 0 10px 24px rgba(15, 23, 42, 0.15);
          }

          .posapp-btn-disabled {
            opacity: 0.62;
            cursor: not-allowed;
          }

          .posapp-stats-grid {
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 14px;
            margin-bottom: 18px;
          }

          .posapp-stat-card {
            background: rgba(255, 255, 255, 0.94);
            border: 1px solid #e5e7eb;
            border-radius: 22px;
            padding: 17px 18px;
            box-shadow: 0 14px 36px rgba(15, 23, 42, 0.06);
            backdrop-filter: blur(8px);
          }

          .posapp-stat-money {
            background: linear-gradient(180deg, #ecfdf5 0%, #ffffff 100%);
            border-color: #bbf7d0;
          }

          .posapp-stat-qty {
            background: linear-gradient(180deg, #eff6ff 0%, #ffffff 100%);
            border-color: #bfdbfe;
          }

          .posapp-stat-average {
            background: linear-gradient(180deg, #fff7ed 0%, #ffffff 100%);
            border-color: #fed7aa;
          }

          .posapp-stat-label {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 11px;
            color: #64748b;
            font-weight: 950;
            text-transform: uppercase;
            letter-spacing: 0.055em;
          }

          .posapp-stat-icon {
            width: 28px;
            height: 28px;
            border-radius: 10px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            background: #f1f5f9;
            font-size: 15px;
          }

          .posapp-stat-value {
            margin-top: 10px;
            font-size: 25px;
            font-weight: 950;
            color: #0f172a;
            line-height: 1;
            word-break: break-word;
          }

          .posapp-stat-subtitle {
            margin-top: 8px;
            font-size: 12px;
            color: #64748b;
            font-weight: 750;
          }

          .posapp-table-card {
            background: #ffffff;
            border: 1px solid #e5e7eb;
            border-radius: 24px;
            padding: 18px;
            box-shadow: 0 16px 42px rgba(15, 23, 42, 0.07);
          }

          .posapp-table-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 16px;
            flex-wrap: wrap;
            margin-bottom: 18px;
          }

          .posapp-section-title {
            margin: 0;
            font-size: 21px;
            font-weight: 950;
            color: #0f172a;
            letter-spacing: -0.02em;
          }

          .posapp-section-subtitle {
            margin: 5px 0 0;
            color: #64748b;
            font-size: 12px;
            font-weight: 750;
          }

          .posapp-search-form {
            display: flex;
            align-items: center;
            gap: 10px;
            flex-wrap: wrap;
          }

          .posapp-search-wrap {
            position: relative;
          }

          .posapp-search-icon {
            position: absolute;
            left: 14px;
            top: 50%;
            transform: translateY(-50%);
            color: #94a3b8;
            font-size: 14px;
          }

          .posapp-search-input {
            width: 310px;
            height: 42px;
            border: 1px solid #d1d5db;
            border-radius: 999px;
            padding: 0 16px 0 38px;
            font-size: 13px;
            outline: none;
            font-weight: 750;
            color: #111827;
            background: #ffffff;
            transition: border 0.2s ease, box-shadow 0.2s ease;
          }

          .posapp-search-input:focus {
            border-color: #38bdf8;
            box-shadow: 0 0 0 4px rgba(56, 189, 248, 0.16);
          }

          .posapp-table-shell {
            border: 1px solid #e5e7eb;
            border-radius: 18px;
            overflow: hidden;
            background: #ffffff;
          }

          .posapp-table-invoice-cell {
            display: flex;
            flex-direction: column;
            gap: 3px;
          }

          .posapp-table-invoice-no {
            font-weight: 950;
            color: #0f172a;
          }

          .posapp-table-invoice-sub {
            font-size: 11px;
            color: #64748b;
            font-weight: 700;
          }

          .posapp-table-qty-pill {
            display: inline-flex;
            background: #eff6ff;
            color: #1d4ed8;
            border: 1px solid #bfdbfe;
            border-radius: 999px;
            padding: 7px 11px;
            font-weight: 950;
            font-size: 12px;
            white-space: nowrap;
          }

          .posapp-table-money-pill {
            display: inline-flex;
            background: #ecfdf5;
            color: #047857;
            border: 1px solid #a7f3d0;
            border-radius: 999px;
            padding: 7px 11px;
            font-weight: 950;
            font-size: 12px;
            white-space: nowrap;
          }

          .posapp-table-payment-pill {
            display: inline-flex;
            background: #f8fafc;
            color: #334155;
            border: 1px solid #e2e8f0;
            border-radius: 999px;
            padding: 7px 11px;
            font-weight: 950;
            font-size: 12px;
            white-space: nowrap;
            text-transform: capitalize;
          }

          .posapp-table-date {
            font-weight: 850;
            color: #0f172a;
            white-space: nowrap;
          }

          .posapp-preview-loading {
            position: fixed;
            right: 18px;
            bottom: 18px;
            z-index: 1300;
            background: #0f172a;
            color: #ffffff;
            border-radius: 999px;
            padding: 10px 16px;
            font-size: 13px;
            font-weight: 900;
            box-shadow: 0 14px 36px rgba(15, 23, 42, 0.25);
          }

          .posapp-export-overlay {
            position: fixed;
            inset: 0;
            z-index: 1300;
            background: rgba(15, 23, 42, 0.58);
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 18px;
          }

          .posapp-export-modal {
            width: min(960px, 96vw);
            max-height: 92vh;
            overflow: auto;
            background: #ffffff;
            border-radius: 22px;
            box-shadow: 0 28px 70px rgba(15, 23, 42, 0.36);
            border: 1px solid #e5e7eb;
          }

          .posapp-export-header,
          .posapp-export-footer {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 14px;
            padding: 18px 20px;
            border-bottom: 1px solid #e5e7eb;
            background: #ffffff;
            flex-wrap: wrap;
          }

          .posapp-export-footer {
            border-top: 1px solid #e5e7eb;
            border-bottom: 0;
            align-items: center;
            position: sticky;
            bottom: 0;
          }

          .posapp-export-title {
            margin: 0;
            color: #0f172a;
            font-size: 20px;
            font-weight: 950;
          }

          .posapp-export-subtitle,
          .posapp-export-hint {
            margin: 5px 0 0;
            color: #64748b;
            font-size: 12px;
            font-weight: 750;
          }

          .posapp-export-close {
            width: 34px;
            height: 34px;
            border: none;
            border-radius: 999px;
            background: #f1f5f9;
            color: #0f172a;
            font-size: 20px;
            font-weight: 900;
            cursor: pointer;
            line-height: 1;
          }

          .posapp-export-body {
            padding: 20px;
            display: grid;
            gap: 18px;
          }

          .posapp-export-section {
            border: 1px solid #e5e7eb;
            border-radius: 16px;
            padding: 15px;
            background: #f8fafc;
          }

          .posapp-export-section-title {
            color: #0f172a;
            font-size: 14px;
            font-weight: 950;
            margin-bottom: 12px;
          }

          .posapp-preset-grid,
          .posapp-column-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
            gap: 10px;
          }

          .posapp-column-grid {
            grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
          }

          .posapp-preset-option,
          .posapp-column-option {
            border: 1px solid #dbe3ef;
            border-radius: 12px;
            padding: 10px 12px;
            background: #ffffff;
            color: #334155;
            font-size: 12px;
            font-weight: 900;
          }

          .posapp-preset-option {
            cursor: pointer;
            text-align: left;
          }

          .posapp-preset-option.active {
            border-color: #0284c7;
            background: #f0f9ff;
            color: #0369a1;
            box-shadow: 0 0 0 3px rgba(2, 132, 199, 0.09);
          }

          .posapp-date-grid {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 12px;
            margin-top: 12px;
          }

          .posapp-export-field {
            display: flex;
            flex-direction: column;
            gap: 6px;
            color: #334155;
            font-size: 12px;
            font-weight: 900;
          }

          .posapp-export-field input {
            height: 40px;
            border: 1px solid #d1d5db;
            border-radius: 10px;
            padding: 0 11px;
            color: #0f172a;
            font-size: 13px;
            font-weight: 800;
            background: #ffffff;
          }

          .posapp-column-option {
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .posapp-column-option input {
            accent-color: #0284c7;
          }

          .posapp-export-actions {
            display: flex;
            gap: 10px;
            flex-wrap: wrap;
          }

          @media (max-width: 1200px) {
            .posapp-stats-grid {
              grid-template-columns: repeat(2, minmax(0, 1fr));
            }
          }

          @media (max-width: 768px) {
            .posapp-page {
              padding: 6px 8px 18px;
            }

            .posapp-hero {
              border-radius: 20px;
              padding: 20px;
            }

            .posapp-title {
              font-size: 25px;
            }

            .posapp-actions,
            .posapp-search-form {
              width: 100%;
              justify-content: stretch;
            }

            .posapp-light-btn,
            .posapp-secondary-btn,
            .posapp-primary-btn {
              flex: 1;
            }

            .posapp-stats-grid {
              grid-template-columns: 1fr;
            }

            .posapp-table-card {
              padding: 14px;
              border-radius: 18px;
            }

            .posapp-table-header {
              align-items: stretch;
            }

            .posapp-search-wrap,
            .posapp-search-input {
              width: 100%;
            }

            .posapp-primary-btn,
            .posapp-secondary-btn,
            .posapp-export-btn {
              width: 100%;
            }

            .posapp-date-grid {
              grid-template-columns: 1fr;
            }

            .posapp-table-shell {
              overflow-x: auto;
            }
          }

          @media (max-width: 480px) {
            .posapp-stat-value {
              font-size: 22px;
            }
          }
        `}
      </style>

      {previewLoading && (
        <div className="posapp-preview-loading">Preparing receipt preview...</div>
      )}

      <div className="posapp-hero">
        <div className="posapp-hero-content">
          <div>
            <div className="posapp-badge">
              <span>●</span>
              <span>POS App Transactions</span>
            </div>

            <h2 className="posapp-title">Transaction History</h2>

            <p className="posapp-description">
              Review POS app invoices, collected totals, product quantities and print thermal receipts from one professional dashboard.
            </p>
          </div>

          <div className="posapp-actions">
            <button
              type="button"
              onClick={handleRefresh}
              disabled={loading}
              className={`posapp-light-btn ${loading ? "posapp-btn-disabled" : ""}`}
            >
              {loading ? "Refreshing..." : "Refresh Transactions"}
            </button>

            <button
              type="button"
              onClick={handleClearSearch}
              className="posapp-secondary-btn"
            >
              Reset View
            </button>
          </div>
        </div>
      </div>

      <div className="posapp-stats-grid">
        {renderStatCard({
          title: "Transactions",
          value: dashboardStats.receiptCount,
          subtitle: "Loaded in current view",
          icon: "🧾",
        })}

        {renderStatCard({
          title: "Money Collected",
          value: safeCurrency(dashboardStats.totalCollected),
          subtitle: "Current POS app result",
          icon: "₹",
          tone: "money",
        })}

        {renderStatCard({
          title: "Products Sold",
          value: dashboardStats.totalQty,
          subtitle: "Total quantity from loaded invoices",
          icon: "📦",
          tone: "qty",
        })}

        {renderStatCard({
          title: "Average Bill",
          value: safeCurrency(dashboardStats.averageReceipt),
          subtitle: "Per POS app invoice",
          icon: "⚡",
          tone: "average",
        })}
      </div>

      <div className="posapp-table-card">
        <div className="posapp-table-header">
          <div>
            <h3 className="posapp-section-title">POS App Receipt List</h3>
            <p className="posapp-section-subtitle">
              Standard invoice table layout with thermal receipt preview on print.
            </p>
          </div>

          <form
            onSubmit={(event) => {
              event.preventDefault();
              setTableRefreshKey((prev) => prev + 1);
            }}
            className="posapp-search-form"
          >
            <div className="posapp-search-wrap">
              <span className="posapp-search-icon">⌕</span>
              <input
                type="text"
                className="posapp-search-input"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search POS transactions..."
              />
            </div>

            <button type="submit" className="posapp-primary-btn">
              Search
            </button>

            <button
              type="button"
              onClick={() => setShowExportModal(true)}
              className="posapp-export-btn"
            >
              Export CSV
            </button>

            {searchQuery && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="posapp-secondary-btn"
                style={{
                  color: "#0284c7",
                  borderColor: "#bae6fd",
                  background: "#f0f9ff",
                }}
              >
                Clear
              </button>
            )}
          </form>
        </div>

        <div className="posapp-table-shell">
          <SalesTable
            key={`${tableRefreshKey}-${searchQuery}`}
            fetchData={fetchPOSInvoiceData}
            columns={columns}
            showAction={true}
            loading={loading}
            handlePrint={(invoiceId) => handlePrint(invoiceId)}
            enablePagination={true}
            rowsPerPage={rowsPerPage}
            showSearch={false}
          />
        </div>
      </div>

      {showExportModal && (
        <div className="posapp-export-overlay" role="dialog" aria-modal="true">
          <div className="posapp-export-modal">
            <div className="posapp-export-header">
              <div>
                <h3 className="posapp-export-title">Custom CSV Export</h3>
                <p className="posapp-export-subtitle">
                  Select date range and columns for POS app receipt export.
                </p>
              </div>
              <button
                type="button"
                className="posapp-export-close"
                onClick={() => setShowExportModal(false)}
                aria-label="Close export modal"
              >
                x
              </button>
            </div>

            <div className="posapp-export-body">
              <div className="posapp-export-section">
                <div className="posapp-export-section-title">Date Range</div>
                <div className="posapp-preset-grid">
                  {datePresetOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`posapp-preset-option ${exportConfig.datePreset === option.value ? "active" : ""}`}
                      onClick={() => updateDatePreset(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>

                <div className="posapp-date-grid">
                  <label className="posapp-export-field">
                    <span>From date</span>
                    <input
                      type="date"
                      value={
                        exportConfig.datePreset === "custom"
                          ? exportConfig.fromDate
                          : formatDateInput(exportRangePreview.from)
                      }
                      disabled={exportConfig.datePreset !== "custom"}
                      onChange={(event) =>
                        setExportConfig((prev) => ({
                          ...prev,
                          datePreset: "custom",
                          fromDate: event.target.value,
                        }))
                      }
                    />
                  </label>

                  <label className="posapp-export-field">
                    <span>To date</span>
                    <input
                      type="date"
                      value={
                        exportConfig.datePreset === "custom"
                          ? exportConfig.toDate
                          : formatDateInput(exportRangePreview.to)
                      }
                      disabled={exportConfig.datePreset !== "custom"}
                      onChange={(event) =>
                        setExportConfig((prev) => ({
                          ...prev,
                          datePreset: "custom",
                          toDate: event.target.value,
                        }))
                      }
                    />
                  </label>
                </div>
              </div>

              <div className="posapp-export-section">
                <div className="posapp-export-section-title">Receipt Columns</div>
                <div className="posapp-column-grid">
                  {receiptExportColumns.map((column) => (
                    <label className="posapp-column-option" key={column.key}>
                      <input
                        type="checkbox"
                        checked={exportConfig.receiptColumns.includes(column.key)}
                        onChange={() => toggleExportColumn("receipt", column.key)}
                      />
                      <span>{column.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="posapp-export-section">
                <div className="posapp-export-section-title">Item Columns</div>
                <div className="posapp-column-grid">
                  {itemExportColumns.map((column) => (
                    <label className="posapp-column-option" key={column.key}>
                      <input
                        type="checkbox"
                        checked={exportConfig.itemColumns.includes(column.key)}
                        onChange={() => toggleExportColumn("item", column.key)}
                      />
                      <span>{column.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="posapp-export-footer">
              <div className="posapp-export-hint">
                Item columns create one CSV row per receipt item. Without item columns, each receipt exports as one row.
              </div>
              <div className="posapp-export-actions">
                <button
                  type="button"
                  className="posapp-secondary-btn"
                  style={{
                    color: "#334155",
                    borderColor: "#cbd5e1",
                    background: "#f8fafc",
                  }}
                  onClick={() => setShowExportModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`posapp-primary-btn ${exportLoading ? "posapp-btn-disabled" : ""}`}
                  disabled={exportLoading}
                  onClick={handleGenerateCsv}
                >
                  {exportLoading ? "Generating..." : "Generate CSV"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showThermalPreview && selectedInvoice && (
        <POSAppThermalInvoicePreview
          open={showThermalPreview}
          onClose={closeThermalPreview}
          invoice={selectedInvoice}
          storeInfo={storeInfo}
        />
      )}
    </div>
  );
}

export default Transaction;
